/**
 * Plugin operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class PluginManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listPlugins(page = 1, size = 20, name?: string): Promise<Record<string, any>> {
    const params: Record<string, any> = { current: page, size };
    if (name) params.name = name;
    return this.session.get('/api/plugin/list', { params });
  }

  async getPluginVersions(pluginUuid: string): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_plugin/extend/versions', { params: { pluginUuid } });
  }

  async getPluginVersion(pluginUuid: string, versionId: string): Promise<Record<string, any>> {
    return this.session.get('/plugin/version', { params: { pluginUuid, versionId } });
  }

  async installPlugin(file: File | Blob): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/plugin/install', { data: formData, headers: {} });
  }

  async uninstallPlugin(pluginId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/plugin/uninstall?pluginId=${pluginId}`);
  }

  async startPlugin(pluginId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/plugin/start?pluginId=${pluginId}`);
  }

  async stopPlugin(pluginId: string): Promise<Record<string, any>> {
    return this.session.post(`/api/plugin/stop?pluginId=${pluginId}`);
  }

  async uploadPlugin(file: File | Blob): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/plugin/upload', { data: formData, headers: {} });
  }

  // --- Extended plugin APIs ---

  async getPlugins(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/plugin/list', { params });
  }

  async getPluginDetail(pluginId: string): Promise<Record<string, any>> {
    return this.session.get(`/api/plugin/${pluginId}`);
  }

  async pagePlugins(
    page = 1,
    size = 20,
    where?: Record<string, any>,
    order?: string[],
  ): Promise<Record<string, any>> {
    const params = {
      page,
      size,
      options: JSON.stringify({ where: where ?? {}, order: order ?? null }),
    };
    return this.session.get('/api/cdp_plugin/extend/page', { params });
  }

  async getPlugin(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp_plugin/extend/get/${uuid}`);
  }

  async createPlugin(data: Record<string, any>, jsFile?: File | Blob): Promise<Record<string, any>> {
    const formData = new FormData();
    if (jsFile) formData.append('jsFile', jsFile);
    formData.append('requestParam', JSON.stringify({ context: {}, data }));
    return this.session.post('/api/cdp_plugin/extend/create', { data: formData, headers: {} });
  }

  async updatePlugin(data: Record<string, any>, jsFile?: File | Blob): Promise<Record<string, any>> {
    const formData = new FormData();
    if (jsFile) formData.append('jsFile', jsFile);
    formData.append('requestParam', JSON.stringify({ context: {}, data }));
    return this.session.post('/api/cdp_plugin/extend/update', { data: formData, headers: {} });
  }

  async deletePlugin(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp_plugin/delete/${uuid}`);
  }

  async batchDeletePlugins(uuids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_plugin/batchDelete', { data: uuids });
  }
}
